import { map, filter, concat, forEach, flatten } from "lodash-es"

const getFileByProperty = (
  value: string,
  fileArr: Array<any>,
  property: string,
) => fileArr.find((item: any) => item[property] === value)

export const getFileById = (objectId: string, fileArr: Array<any>) =>
  getFileByProperty(objectId, fileArr, "objectId")

export const getFileByFilenetId = (filenetId: string, fileArr: Array<any>) =>
  getFileByProperty(filenetId, fileArr, "filenetId")

export const getExtensionsByMimeType = (mimeTypes: string[]) => {
  const mimeTypeArr = flatten([mimeTypes])
  let extList: string[] = []
  forEach(mimeTypeArr, function (mimeType) {
    extList = concat(
      extList,
      map(filter(mimeTypeList, { mimeType: mimeType }), "extension"),
    )
  })
  return extList
}

export const base64toBlob = (
  base64Data: any,
  contentType: string,
  sliceSize = 512,
): Blob => {
  const byteCharacters = atob(base64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = [slice.length]
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.codePointAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)

    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, { type: contentType })

  return blob
}

export const mimeTypeList = [
  { extension: ".aac", desc: "AAC audio file", mimeType: "audio/aac" },
  {
    extension: ".abw",
    desc: "AbiWord document",
    mimeType: "application/x-abiword",
  },
  {
    extension: ".arc",
    desc: "Archive document (multiple files embedded)",
    mimeType: "application/octet-stream",
  },
  {
    extension: ".avi",
    desc: "AVI: Audio Video Interleave",
    mimeType: "video/x-msvideo",
  },
  {
    extension: ".azw",
    desc: "Amazon Kindle eBook format",
    mimeType: "application/vnd.amazon.ebook",
  },
  {
    extension: ".bin",
    desc: "Any kind of binary data",
    mimeType: "application/octet-stream",
  },
  { extension: ".bmp", desc: "Bitmap", mimeType: "image/bmp" },
  { extension: ".bz", desc: "BZip archive", mimeType: "application/x-bzip" },
  {
    extension: ".bz2",
    desc: "BZip2 archive",
    mimeType: "application/x-bzip2",
  },
  {
    extension: ".csh",
    desc: "C-Shell script",
    mimeType: "application/x-csh",
  },
  {
    extension: ".css",
    desc: "Cascading Style Sheets (CSS)",
    mimeType: "text/css",
  },
  {
    extension: ".csv",
    desc: "Comma-separated values (CSV)",
    mimeType: "text/csv",
  },
  {
    extension: ".doc",
    desc: "Microsoft Word",
    mimeType: "application/msword",
  },
  {
    extension: ".docx",
    desc: "Microsoft Word (OpenXML)",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    extension: ".eot",
    desc: "MS Embedded OpenType fonts",
    mimeType: "application/vnd.ms-fontobject",
  },
  {
    extension: ".epub",
    desc: "Electronic publication (EPUB)",
    mimeType: "application/epub+zip",
  },
  {
    extension: ".gif",
    desc: "Graphics Interchange Format (GIF)",
    mimeType: "image/gif",
  },
  {
    extension: ".htm",
    desc: "HyperText Markup Language (HTML)",
    mimeType: "text/html",
  },
  {
    extension: ".html",
    desc: "HyperText Markup Language (HTML)",
    mimeType: "text/html",
  },
  { extension: ".ico", desc: "Icon format", mimeType: "image/x-icon" },
  { extension: ".ics", desc: "iCalendar format", mimeType: "text/calendar" },
  {
    extension: ".jar",
    desc: "Java Archive (JAR)",
    mimeType: "application/java-archive",
  },
  { extension: ".jpeg", desc: "JPEG images", mimeType: "image/jpeg" },
  { extension: ".jpg", desc: "JPEG images", mimeType: "image/jpeg" },
  {
    extension: ".js",
    desc: "JavaScript (ECMAScript)",
    mimeType: "application/javascript",
  },
  { extension: ".json", desc: "JSON format", mimeType: "application/json" },
  {
    extension: ".mid",
    desc: "Musical Instrument Digital Interface (MIDI)",
    mimeType: "audio/midi",
  },
  {
    extension: ".midi",
    desc: "Musical Instrument Digital Interface (MIDI)",
    mimeType: "audio/midi",
  },
  { extension: ".mpeg", desc: "MPEG Video", mimeType: "video/mpeg" },
  {
    extension: ".mpkg",
    desc: "Apple Installer Package",
    mimeType: "application/vnd.apple.installer+xml",
  },
  {
    extension: ".odp",
    desc: "OpenDocument presentation document",
    mimeType: "application/vnd.oasis.opendocument.presentation",
  },
  {
    extension: ".ods",
    desc: "OpenDocument spreadsheet document",
    mimeType: "application/vnd.oasis.opendocument.spreadsheet",
  },
  {
    extension: ".odt",
    desc: "OpenDocument text document",
    mimeType: "application/vnd.oasis.opendocument.text",
  },
  { extension: ".oga", desc: "OGG audio", mimeType: "audio/ogg" },
  { extension: ".ogv", desc: "OGG video", mimeType: "video/ogg" },
  { extension: ".ogx", desc: "OGG", mimeType: "application/ogg" },
  { extension: ".otf", desc: "OpenType font", mimeType: "font/otf" },
  {
    extension: ".png",
    desc: "Portable Network Graphics",
    mimeType: "image/png",
  },
  {
    extension: ".pdf",
    desc: "Adobe Portable Document Format (PDF)",
    mimeType: "application/pdf",
  },
  {
    extension: ".ppt",
    desc: "Microsoft PowerPoint",
    mimeType: "application/vnd.ms-powerpoint",
  },
  {
    extension: ".pptx",
    desc: "Microsoft PowerPoint (OpenXML)",
    mimeType:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  {
    extension: ".rar",
    desc: "RAR archive",
    mimeType: "application/x-rar-compressed",
  },
  { extension: ".rtf", desc: "Rich Text Format (RTF)", mimeType: "text/rtf" },
  {
    extension: ".rtf",
    desc: "Rich Text Format (RTF)",
    mimeType: "application/rtf",
  },
  {
    extension: ".sh",
    desc: "Bourne shell script",
    mimeType: "application/x-sh",
  },
  {
    extension: ".svg",
    desc: "Scalable Vector Graphics (SVG)",
    mimeType: "image/svg+xml",
  },
  {
    extension: ".swf",
    desc: "Small web format (SWF) or Adobe Flash document",
    mimeType: "application/x-shockwave-flash",
  },
  {
    extension: ".tar",
    desc: "Tape Archive (TAR)",
    mimeType: "application/x-tar",
  },
  {
    extension: ".tif",
    desc: "Tagged Image File Format (TIFF)",
    mimeType: "image/tiff",
  },
  {
    extension: ".tiff",
    desc: "Tagged Image File Format (TIFF)",
    mimeType: "image/tiff",
  },
  {
    extension: ".ts",
    desc: "Typescript file",
    mimeType: "application/typescript",
  },
  { extension: ".ttf", desc: "TrueType Font", mimeType: "font/ttf" },
  { extension: ".txt", desc: "Text", mimeType: "text/plain" },
  {
    extension: ".vsd",
    desc: "Microsoft Visio",
    mimeType: "application/vnd.visio",
  },
  {
    extension: ".wav",
    desc: "Waveform Audio Format",
    mimeType: "audio/x-wav",
  },
  { extension: ".weba", desc: "WEBM audio", mimeType: "audio/webm" },
  { extension: ".webm", desc: "WEBM video", mimeType: "video/webm" },
  { extension: ".webp", desc: "WEBP image", mimeType: "image/webp" },
  {
    extension: ".woff",
    desc: "Web Open Font Format (WOFF)",
    mimeType: "font/woff",
  },
  {
    extension: ".woff2",
    desc: "Web Open Font Format (WOFF)",
    mimeType: "font/woff2",
  },
  { extension: ".xhtml", desc: "XHTML", mimeType: "application/xhtml+xml" },
  {
    extension: ".xls",
    desc: "Microsoft Excel",
    mimeType: "application/vnd.ms-excel",
  },
  {
    extension: ".xlsx",
    desc: "Microsoft Excel (OpenXML)",
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  { extension: ".xml", desc: "XML", mimeType: "application/xml" },
  {
    extension: ".xul",
    desc: "XUL",
    mimeType: "application/vnd.mozilla.xul+xml",
  },
  { extension: ".zip", desc: "ZIP archive", mimeType: "application/zip" },
  {
    extension: ".3gp",
    desc: "3GPP audio/video container",
    mimeType: "video/3gpp",
  },
  {
    extension: ".3gp",
    desc: "3GPP audio/video container",
    mimeType: "audio/3gpp",
  },
  {
    extension: ".3g2",
    desc: "3GPP2 audio/video container",
    mimeType: "video/3gpp2",
  },
  {
    extension: ".3g2",
    desc: "3GPP2 audio/video container",
    mimeType: "audio/3gpp2",
  },
  {
    extension: ".7z",
    desc: "7-zip archive",
    mimeType: "application/x-7z-compressed",
  },
]

export const getAllowedMimeTypes = () => {
  return [
    "image/jpeg",
    "image/tiff",
    "image/bmp",
    "image/png",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "application/rtf",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-outlook",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "application/vnd.eszigno3+xml",
  ]
}
